import type { GameOutcome } from '../games/core/types';
import type { RunAction, RunState } from './types';
import { RUN_SCHEMA_VERSION } from './types';

export function createEmptyRun(): RunState {
  return {
    schemaVersion: RUN_SCHEMA_VERSION,
    id: '',
    seed: '',
    mode: 'practice',
    phase: 'preparing',
    phaseRevision: 0,
    phaseStartedAt: 0,
    createdAt: 0,
    updatedAt: 0,
    puzzles: [],
    currentIndex: 0,
    gameStates: {},
    scoreEvents: [],
    results: [],
    rawActiveMs: 0,
    activeSinceMs: null,
    sessionElapsedMs: 0,
    sessionSinceMs: null,
    generationProgress: { completed: 0, total: 11, gameName: '' },
    error: null,
  };
}

export function runReducer(state: RunState, action: RunAction): RunState {
  switch (action.type) {
    case 'hydrate':
      return action.state;

    case 'create':
      return {
        ...createEmptyRun(),
        id: action.id,
        seed: action.seed,
        mode: action.mode,
        createdAt: action.nowEpoch,
        updatedAt: action.nowEpoch,
        phaseStartedAt: action.nowEpoch,
      };

    case 'generation-progress':
      if (state.phase !== 'preparing') return state;
      return {
        ...state,
        generationProgress: {
          completed: action.completed,
          total: action.total,
          gameName: action.gameName,
        },
        updatedAt: action.nowEpoch,
      };

    case 'prepared':
      if (state.phase !== 'preparing') return state;
      return transition(state, 'splash', action.nowEpoch, {
        puzzles: action.puzzles,
        sessionSinceMs: action.nowPerformance,
      });

    case 'retry-generation':
      if (state.phase !== 'error' || state.puzzles.length > 0) return state;
      return transition(state, 'preparing', action.nowEpoch, {
        error: null,
        generationProgress: { completed: 0, total: 11, gameName: '' },
        rawActiveMs: 0,
        activeSinceMs: null,
        sessionElapsedMs: 0,
        sessionSinceMs: null,
      });

    case 'discard':
      return createEmptyRun();

    case 'splash-complete':
      if (state.phase !== 'splash') return state;
      return transition(state, 'playing', action.nowEpoch, {
        activeSinceMs: action.nowPerformance,
      });

    case 'save-game-state':
      if (state.phase !== 'playing') return state;
      return {
        ...state,
        gameStates: {
          ...state.gameStates,
          [String(state.currentIndex)]: action.state,
        },
        updatedAt: action.nowEpoch,
      };

    case 'charge': {
      if (state.phase !== 'playing') return state;
      if (state.scoreEvents.some((event) => event.id === action.id)) return state;
      return {
        ...state,
        scoreEvents: [
          ...state.scoreEvents,
          {
            id: action.id,
            gameIndex: state.currentIndex,
            amountMs: action.amountMs,
            label: action.label,
            createdAt: action.nowEpoch,
          },
        ],
        updatedAt: action.nowEpoch,
      };
    }

    case 'complete-game': {
      if (state.phase !== 'playing' || !state.puzzles[state.currentIndex]) return state;
      if (state.results[state.currentIndex]) return state;
      const rawTimeMs = getActiveRawMs(state, action.nowPerformance);
      const outcomeEventId = `outcome:${state.currentIndex}`;
      const scoreEvents = state.scoreEvents.some((event) => event.id === outcomeEventId)
        ? state.scoreEvents
        : [
            ...state.scoreEvents,
            {
              id: outcomeEventId,
              gameIndex: state.currentIndex,
              amountMs: action.baseDeltaMs,
              label: outcomeLabel(action.outcome),
              createdAt: action.nowEpoch,
            },
          ];
      const deltaMs = scoreEvents
        .filter((event) => event.gameIndex === state.currentIndex)
        .reduce((total, event) => total + event.amountMs, 0);
      const result = {
        gameId: state.puzzles[state.currentIndex].gameId,
        rawTimeMs,
        deltaMs,
        outcome: action.outcome,
        message: action.message,
        metadata: action.metadata ?? {},
      };
      return transition(state, 'outcome', action.nowEpoch, {
        activeSinceMs: null,
        rawActiveMs: rawTimeMs,
        scoreEvents,
        results:
          state.results.length === state.currentIndex
            ? [...state.results, result]
            : state.results.map((existing, index) =>
                index === state.currentIndex ? result : existing,
              ),
      });
    }

    case 'outcome-complete':
      if (state.phase !== 'outcome') return state;
      return transition(state, 'loading', action.nowEpoch);

    case 'loading-complete':
      if (state.phase !== 'loading') return state;
      if (state.currentIndex >= state.puzzles.length - 1) {
        return transition(state, 'results', action.nowEpoch, {
          sessionElapsedMs: getSessionElapsedMs(state, action.nowPerformance),
          sessionSinceMs: null,
        });
      }
      return transition(state, 'splash', action.nowEpoch, {
        currentIndex: state.currentIndex + 1,
        rawActiveMs: 0,
        activeSinceMs: null,
      });

    case 'fail':
      if (state.phase === 'error' || state.phase === 'results') return state;
      return transition(state, 'error', action.nowEpoch, {
        error: action.message,
        activeSinceMs: null,
        rawActiveMs: getActiveRawMs(state, action.nowPerformance),
        sessionElapsedMs: getSessionElapsedMs(state, action.nowPerformance),
        sessionSinceMs: null,
      });

    default:
      return state;
  }
}

function transition(
  state: RunState,
  phase: RunState['phase'],
  nowEpoch: number,
  changes: Partial<RunState> = {},
): RunState {
  return {
    ...state,
    ...changes,
    phase,
    phaseRevision: state.phaseRevision + 1,
    phaseStartedAt: nowEpoch,
    updatedAt: nowEpoch,
  };
}

function outcomeLabel(outcome: GameOutcome['kind']): string {
  if (outcome === 'skipped') return 'Skip penalty';
  if (outcome === 'failed') return 'Failure result';
  return 'Completion result';
}

export function getActiveRawMs(state: RunState, nowPerformance: number): number {
  if (state.phase !== 'playing' || state.activeSinceMs === null) return state.rawActiveMs;
  return state.rawActiveMs + Math.max(0, nowPerformance - state.activeSinceMs);
}

export function getSessionElapsedMs(state: RunState, nowPerformance: number): number {
  if (state.sessionSinceMs === null) return state.sessionElapsedMs;
  return state.sessionElapsedMs + Math.max(0, nowPerformance - state.sessionSinceMs);
}

export function getRunTotalMs(state: RunState): number {
  return state.results.reduce((total, result) => total + result.rawTimeMs + result.deltaMs, 0);
}

export function isRunLedgerReconciled(state: RunState): boolean {
  if (new Set(state.scoreEvents.map((event) => event.id)).size !== state.scoreEvents.length) {
    return false;
  }
  return state.results.every((result, index) => {
    if (result.gameId !== state.puzzles[index]?.gameId) return false;
    const ledgerDelta = state.scoreEvents
      .filter((event) => event.gameIndex === index)
      .reduce((total, event) => total + event.amountMs, 0);
    return ledgerDelta === result.deltaMs;
  });
}

export function createChargeId(state: RunState, key: string): string {
  return `${state.currentIndex}:${key}`;
}

export function completeFromOutcome(
  outcome: GameOutcome,
  nowEpoch: number,
  nowPerformance: number,
): RunAction {
  return {
    type: 'complete-game',
    outcome: outcome.kind,
    baseDeltaMs: outcome.baseDeltaMs,
    message: outcome.message,
    metadata: outcome.metadata,
    nowEpoch,
    nowPerformance,
  };
}
